import { Injectable } from '@angular/core';
import { ContentService } from '@blk/cwp-ng-framework';
import { StoreService } from '@ddt-web/store-service';
import { chain } from 'lodash';
import { combineLatest, Observable, of, ReplaySubject, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';

import { AppStoreConfig, AppTypedStoreConfig, ColumnConfig, TabConfig } from '../models';
import { CellData } from '../models/cell-data';
import { ComponentData } from '../models/component-data';
import { ComponentDataFactory } from '../models/component-data-factory';
import { ErrorData } from '../models/error-data';
import { StoreColumnExtractor } from '../models/extractors';
import { CellRow } from '../models/interfaces';
import { TabData } from '../models/tab-data';
import { TabUtil } from '../utils';
import { CellUtil } from '../utils/cell-util';

/*******************************************
 * HERE BE DRAGONS - NO DISPLAY COMPONENT SPECIFIC LOGIC ALLOWED
 * Under no circumstances DO NOT change the code below if it looks like it doesn't do what you need it to do.
 * Consult the team if there's functionality missing !!!
 *******************************************/

@Injectable()
export class ViewDataFactoryService {
	private factories: Map<string, ComponentDataFactory> = new Map();

	private componentInstanceCounter: Map<string, number> = new Map();
	private _usedStoreRegions$: Subject<string[]> = new ReplaySubject(1);
	private _registeredStoreConfigs: AppStoreConfig | AppTypedStoreConfig = {};

	constructor(private storeSvc: StoreService) { }

	public registerAppStoreConfig(newConfig: AppStoreConfig | AppTypedStoreConfig): void {
		for (const storeName in newConfig) {
			if (newConfig.hasOwnProperty(storeName) && this._registeredStoreConfigs.hasOwnProperty(storeName)) {
				console.warn(`DATA: Store region ${storeName} is already registered!`);
			}
		}
		Object.assign(this._registeredStoreConfigs, newConfig);
	}

	public get usedStoreRegions$(): Observable<string[]> {
		return this._usedStoreRegions$.asObservable().pipe(distinctUntilChanged());
	}

	public componentUsed(...componentIds: string[]): void {
		componentIds.forEach(componentId => {
			if (!this.componentInstanceCounter.has(componentId)) {
				this.componentInstanceCounter.set(componentId, 1);
			} else {
				this.componentInstanceCounter.set(componentId, this.componentInstanceCounter.get(componentId) + 1);
			}
		});
	}
	public componentDisposed(componentInfo: any): void {
		const componentId = componentInfo && componentInfo.componentId ? componentInfo['componentId'] : componentInfo;
		if (!this.componentInstanceCounter.has(componentId)) {
			console.warn(`DATA: Unknown component instance of ${componentId} was disposed of.`);
		} else {
			const instances = this.componentInstanceCounter.get(componentId);
			if (instances - 1 > 0) {
				this.componentInstanceCounter.set(componentId, -1);
			} else {
				this.componentInstanceCounter.delete(componentId);
			}
		}
	}

	public getComponentData(
		componentId: string,
		tabConfigs: TabConfig[],
		portfolioDataConfig: ColumnConfig<StoreColumnExtractor>[],
		idsObservable$: Observable<string[]>,
		contentSvc?: ContentService
	): Observable<ComponentData> {
		contentSvc = contentSvc ? contentSvc : new ContentService(null);
		if (!tabConfigs && !this.factories.has(componentId)) {
			throw new Error(`Unknown component '${componentId}'`);
		}
		const factory = this.factories.get(componentId);

		const storeLookupTemp: any[] = chain(factory ? factory.storeConfigDependencies() : this.componentStoreDependencies(tabConfigs, portfolioDataConfig))
			.map(configName => {
				if (!this._registeredStoreConfigs[configName]) {
					throw new Error(`Component ${componentId} depends on unregistered store region ${configName}, check your application setup!`);
				}
				if (this._registeredStoreConfigs[configName].portfolioIndependent) {
					return [
						configName,
						combineLatest(idsObservable$, this.storeSvc.selectAll(this._registeredStoreConfigs[configName])).pipe(
							filter(([ids, data]) => ids && ids.length > 0),
							map(([ids, data]) => {
								return ids.map(id => {
									if (data) {
										const value = {
											object: data,
											isLoading: false
										};
										return value;
									}
									return Object.assign({}, { isLoading: false });
								});
							})
						)
					];
				} else {
					return [
						configName,
						idsObservable$.pipe(
							filter(ids => ids && ids.length > 0),
							switchMap(ids => {
								return this.storeSvc.selectByIds(this._registeredStoreConfigs[configName], ids).pipe(
									map(data => {
										return ids.map(id => {
											const value =
												data && data.entities && data.entities[id] ? data.entities[id] : this._registeredStoreConfigs[configName].defaultValue || {};
											return Object.assign({ ...value }, { isLoading: data.isLoading });
										});
									})
								);
							})
						)
					];
				}
			})
			.unzip()
			.value();

		return combineLatest(of(storeLookupTemp[0]), ...storeLookupTemp[1]).pipe(
			map(args => {
				const prep = [args[0], args.slice(1)];
				const postZip = chain(prep)
					.unzip()
					.value();
				const zippedLookup = new Map(postZip);
				return <Map<string, any[]>>zippedLookup;
			}),

			// hack for preventing retrigerring component loads
			debounceTime(100),

			map((storeLookup: Map<string, any[]>) => {
				try {
					const isLoading = !Array.from(storeLookup.values()).every(values => values.every(data => !data.isLoading));

					if (isLoading) {
						return new ComponentData(componentId, [], [], true);
					}
					if (factory) {
						// get factory component config to check existing data error/loading in progress
						console.log(`DATA: Using component data factory impl for ${componentId}`);
						return factory.getComponentData(storeLookup, contentSvc);

						// Prefer factory impl over the component config
					}
					// Proceed with the component static config
					const portfolioColumnData = portfolioDataConfig.map(column => {
						const dataArgs = column.extractor.storeConfigDependencies().map(config => storeLookup.get(config));
						const portsDataArgs = chain(dataArgs)
							.unzip()
							.value();
						const extracted = portsDataArgs.map(portfolioData => column.extractor.extractor()(...portfolioData));
						const columnData: CellData[] = [];
						extracted.forEach(portValue => {
							if (portValue) {
								columnData.push(new CellData(column, portValue, column.labelFormatter.format(portValue, contentSvc)));
							} else {
								columnData.push(new CellData(column, portValue));
							}
						});
						return columnData;
					});
					const portfolioData: CellRow[] = chain(portfolioColumnData)
						.unzip()
						.map((portfolioCells: CellData[]) => CellUtil.getCellRow(portfolioCells))
						.value();
					const tabs = tabConfigs.map((tab, index) => {
						if (tab && tab.extractorConfig) {
							try {
								tab = new TabConfig(
									tab.id,
									tab.title
										? new ColumnConfig(
											CellUtil.translateColumnText(tab.title.label, contentSvc),
											CellUtil.translateColumnText(tab.title.infoBubble, contentSvc),
											tab.title.extractor,
											tab.title.id,
											tab.title.labelFormatter,
											tab.title.formatters,
											tab.title.visible
										)
										: null,
									tab.extractorConfig
								);

								const tableDataList = tab.extractorConfig.extract(storeLookup, contentSvc);
								// NOTE: We consider tab has error when all rawValue & formattedValue in table are empty
								if (tableDataList.every(tableData => TabUtil.isEmptyTable(tableData))) {
									console.warn(`DATA: Error getting data for ${componentId}:${tab.id} no data came back for this tab`);
									return new TabData(tab, [], null, new ErrorData(tab.id, false, false, [], 'Error getting tab data', 'Data unavailable for chosen portfolios'));
								} else {
									return new TabData(tab, tableDataList, null);
								}
							} catch (error) {
								console.error(`DATA: Error occurred while extracting data for a tab ${componentId}:${tab.id}`, error);
								return new TabData(
									tab,
									[],
									null,
									new ErrorData(tab.id, false, false, [], 'Error getting tab data', `Error occurred while extracting data for a tab ${componentId}:${tab.id}`)
								);
							}
						} else {
							const errorMsg = `No extractor config for ${componentId}:${tab.id}`;
							console.error('DATA: ' + errorMsg);
							return new TabData(tab, [], null, new ErrorData(tab ? tab.id : `${componentId}:${index}`, false, false, [], 'Error getting tab data', errorMsg));
						}
					});
					if (tabs.every(tab => !(tab && !tab.error))) {
						console.warn(`DATA: Error getting data for ${componentId} no data came back any of the tabs`);
						return new ComponentData(
							componentId,
							[],
							portfolioData,
							false,
							new ErrorData(componentId, false, false, [], 'Error getting component data', 'Data unavailable for chosen portfolios')
						);
					} else {
						return new ComponentData(componentId, tabs, portfolioData, false);
					}
				} catch (error) {
					console.error(`DATA: Error occurred while extracting data for a component ${componentId}`, error);
					return new ComponentData(
						componentId,
						[],
						[],
						false,
						new ErrorData(componentId, false, false, [], 'Error getting component data', 'Error occurred while extracting data for a component')
					);
				}
			})
		);
	}

	private componentStoreDependencies(tabConfigs: TabConfig[], portfolioDataConfig: ColumnConfig<StoreColumnExtractor>[]): string[] {
		return chain(tabConfigs)
			.flatMap(tab => (tab.extractorConfig ? tab.extractorConfig.storeDependencies() : []))
			.concat(portfolioDataConfig ? portfolioDataConfig.map(column => column.extractor.storeConfigDependencies()) : [])
			.flattenDeep()
			.uniq()
			.value();
	}

	public registerFactoryService(componentDataFactory: ComponentDataFactory): void {
		this.factories.set(componentDataFactory.getComponentId(), componentDataFactory);
	}
}
